import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { WIZ_EVENT_TYPE,  
  CONFIRMATION_MODE, CONFIRMATION_RESULT, WizMathUtils } from '@wizeoo/akm-core-lib';
import { BATCH_FILE_FORMAT, ConfirmationResultObj, FilterComponent, WizButton, WizDatasource, 
  WizFocusManager, WizGroupCommand, WizIcon, WizPopUp, WizTextInput, WizTranslationInput, 
  WizValidationContainerDirective, WizValidationError, WizValidationErrors, WizValidatorFn, WizVgroup, WizEventManager, WizEvent } from '@wizeoo/akm-component-lib';
import { FindWeightGridCommand } from '@wizeoo/akm-common-lib';
import { MultiDisplay, MultiDisplayConfig, WeightGridVO } from '@wizeoo/akm-common-lib';
import { AgLabelValidityRenderer } from '@wizeoo/akm-common-lib';
import { AlkEvent } from '@wizeoo/akm-common-lib';
import { BaseView } from '@wizeoo/akm-common-lib';
import { BATCH_KIND } from '@wizeoo/akm-common-lib';
import { UserApplicationVO, StaticFeatureEnum, PermissionAccess, PermissionVisibility, ViewPermission } from '@wizeoo/akm-common-lib';
import { ALK_EVENT_TYPE } from '@wizeoo/akm-common-lib';
import { Model } from 'src/app/model/model';
import { ViewData } from '@wizeoo/akm-common-lib';

/**
 * Vue de gestion des grilles de poids
 */
@Component({
  selector: 'wg-view',
  templateUrl: './wg.view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WgView extends BaseView {
  // zone détail
  @ViewChild('vgDetailZone', { read: WizVgroup, static: false })
  vgDetailZone: WizVgroup;
  @ViewChild('vgDetailZone', {
    read: WizValidationContainerDirective,
    static: false,
  })
  private vgDetailZoneValidator: WizValidationContainerDirective;

  @ViewChild('multiDisp', { read: MultiDisplay })
  multiDisp: MultiDisplay<WeightGridVO>;
  /** Button d'ajout de profil */
  @ViewChild('btnAddWg', { read: WizIcon }) btnAddWg: WizIcon;
  /** Bouton d'export */
  @ViewChild('btnExport', { read: WizIcon }) btnExport: WizIcon;
  /** Bouton d'import */
  @ViewChild('btnImport', { read: WizIcon }) btnImport: WizIcon;

  /** Text input de saisie du code */
  @ViewChild('tiCode', { read: WizTextInput }) tiCode: WizTextInput;
  /** Composant de saisie du libellé traductible */
  @ViewChild('tiLabelTrs', { read: WizTranslationInput })
  tiLabelTrs: WizTranslationInput;

  /** Bouton de sauvegarde */
  @ViewChild('btnSave', { read: WizButton }) btnSave: WizButton;
  /** Bouton d'annulation de saisie */
  @ViewChild('btnCancel', { read: WizButton }) btnCancel: WizButton;
  /** Bouton suppression */
  @ViewChild('btnRemove', { read: WizIcon }) btnRemove: WizIcon;

  public wgDS: WizDatasource<WeightGridVO> = new WizDatasource<WeightGridVO>([])
    .setSortFields([{ property: 'code', way: 'asc' }])
    .setFilter(this.wgFilterFunc.bind(this))
    .build();

  constructor(elementRef: ElementRef) {
    super(elementRef);
  }


  /* #############################################################
                        CYCLE DE VIE WizView
     ############################################################# */

  /**
   * Pretraitement: chargement des données nécessaires à la vue
   * @return WizGroupCommand
   */
  public wizBeforeInit(): WizGroupCommand {
    super.wizBeforeInit();
    const commands = [];
    // disponibilité des chronocodes pour labels
    if (!Model.getInstance().weightGridEngine.getWeightGridMap()) {
      commands.push(new FindWeightGridCommand(Model.getInstance()));
    }

    if (commands.length > 0) {
      return new WizGroupCommand().configure(commands);
    } else {
      return null;
    }
  }

  private get viewPermissions(): ViewPermission[] {
    return [
      {
        feature: StaticFeatureEnum.WG,
        permissions: [
          {
            component: this.btnAddWg,
            accesses: [PermissionAccess.CREATE],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.vgDetailZone,
            accesses: [PermissionAccess.UPDATE, PermissionAccess.CREATE],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnRemove,
            accesses: [PermissionAccess.DELETE],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnExport,
            accesses: [PermissionAccess.BATCH],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnImport,
            accesses: [PermissionAccess.BATCH],
            visibility: PermissionVisibility.DISABLED,
          },
        ],
      },
    ];
  }

  /**
   * Synchronisation des données
   */
  public wizSynchronizeData(): void {
    this.tiCode.wizRestrict = ViewData.CODE_REGEXP;
    this.wgDS.setSource([...Model.getInstance().weightGridEngine.getWeightGrids()]);
  }

  /**
   * Mise en place des écouteurs
   */
  public wizAddListener(): void {
    super.wizAddListener();
    // merge (create / update sur btnSave)
    WizEventManager.addListener(this, this.btnSave, WIZ_EVENT_TYPE.CLICK, this.doMerge);
    // revert (btCancel)
    WizEventManager.addListener(this, this.btnCancel, WIZ_EVENT_TYPE.CLICK, this.doRevert);
    // écouteur global change pour modelUpdate
    WizEventManager.addListener(this, this.vgDetailZone, WIZ_EVENT_TYPE.MODEL_UPDATE, this.updateModel);

  }


  /**
   * Suppression des écouteurs
   */
  public wizRemoveListener(): void {
    super.wizRemoveListener();
    // merge (create / update sur btnSave)
    WizEventManager.removeListener(this, this.btnSave, WIZ_EVENT_TYPE.CLICK);
    // revert (btCancel)
    WizEventManager.removeListener(this, this.btnCancel, WIZ_EVENT_TYPE.CLICK);
    WizEventManager.removeListener(this, this.vgDetailZone, WIZ_EVENT_TYPE.MODEL_UPDATE);
  }


  /* ##############################################################################
                    Panneau gauche : Liste, sélection, filtres
     ############################################################################## */

  /**
   * configuration de l'affichage en multi liste
   */
  public multiDisplayConfig: MultiDisplayConfig<WeightGridVO> = {
    model: Model.getInstance(),
    datasource: this.wgDS,
    defaultMode: 'grid',
    onSelectionChanging: this.onWizEventSelectionChange.bind(this),
    grid: {
      enabled: true,
      columns: [
        {
          headerName: '$.common.code.$',
          field: 'code',
          width: 100,
          sortable: true,
        },
        {
          headerName: '$.common.label.$',
          field: 'trsLabel',
          flex: 1,
          cellRenderer: 'labelValidityRenderer',
          sortable: true,
        },
      ],
      renderers: {
        labelValidityRenderer: AgLabelValidityRenderer,
      },
    },
    filter: {
      enabled: true,
      initFilter: this.initFilter.bind(this),
    },
  };

  private mainFilter: FilterComponent;

  private initFilter(pFilter: FilterComponent): FilterComponent {
    this.mainFilter = pFilter;
    return this.mainFilter;
  }


  /**
   * Callback de selection dans le grid
   * @param pEvent WizEvent
   */
  private onWizEventSelectionChange(e: WizEvent): void {
    // empêche le changement de selection quand il y a une modification de model en cours
    if (ViewData.wizUpdateModel) {
      // on stop le fonctionnement par défaut pour afficher la pop, on sélectionnera l'item seulement si le user confirme
      e.preventDefault();

      WizPopUp.confirm(
        '$.common.pending.update.title.$',
        '$.common.pending.update.detail.$',
        this.confirmSwap.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        e.value,
        null,
        Model.getInstance().cloneWG
      ).show();
    } else {
      if (!this.wgDS.isSelected(e.value)) {
        // pas encore sélectionné
        this.doSelect(e.value);
      } else {
        // déjà sélectionné, déselection
        this.doSelect(null);
      }
    }
  }

  /**
   * Confirmation de la sélection de wg
   * @param result: Objet ConfirmationResultObj contenant le choix effectué sur la popup
   */
  private confirmSwap(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      this.doSelect(result.data);
      this.wgDS.select([result.data]);
      this.feed();
    }
  }


  private doSelect(wg: WeightGridVO) {
    Model.getInstance().currentWG = wg;
    Model.getInstance().cloneWG = !!Model.getInstance().currentWG ? Model.getInstance().weightGridEngine.cloneWG(Model.getInstance().currentWG) : null;
    this.feed();
  }


  /**
   * construction de la zone de filtre des visions
   * @param event : Evenement de changement dans le text input de filtre
   */
  private buildFilter(_: WizEvent = null): void {
    this.mainFilter.configure(['code', 'trsLabel'], null, null);
    // le contextEngine prend le relais pour alimenter les filtres par défaut
    Model.getInstance().userContextService.feedFilterContext(this.mainFilter);
  }






  public doAct(action: string): void {
    switch (action) {
      case 'export':
        Model.getInstance().batchEngine.export(BATCH_KIND.WG, BATCH_FILE_FORMAT.JSON);
        break;
      case 'import':
        Model.getInstance().batchEngine.import(BATCH_KIND.WG);
        break;
      case 'delete':
        this.askForDelete();
        break;
      case 'create': // demande de create
        this.askForAdd();
        break;
    }
  }




  /* #########################################
                    Detail
     ######################################### */

  public get isDetailVisible() {
    const result =
      !!Model.getInstance().currentWG &&
      Model.getInstance().permissionEngine.hasAnyPermission({
        feature: StaticFeatureEnum.WG,
        accesses: [PermissionAccess.READ, PermissionAccess.CREATE, PermissionAccess.UPDATE],
        visibility: PermissionVisibility.DISABLED,
      });
    return result;
  }

  /**
   * filtre la liste des wgs
   * @param item wgVO
   * @returns boolean, true si l'element correspond au filtre, false sinon
   */
  private wgFilterFunc(item: WeightGridVO): boolean {

    return true;
  }

  /* #############################################################
                 persistence
     ############################################################# */

  /**
   * clic sur le bouton "ajouter une wg"
   * @param e void
   */
  private askForAdd(): void {
    Model.getInstance().currentWG = Model.getInstance().weightGridEngine.buildFakeWG();
    Model.getInstance().cloneWG = Model.getInstance().weightGridEngine.cloneWG(Model.getInstance().currentWG);
    this.feed();
  }



  /** demande de suppression la wg */
  public askForDelete(): void {
    WizPopUp.confirm(
      '$.common.delete.title.$',
      '$.common.delete.detail.$',
      this.doRemove.bind(this),
      CONFIRMATION_MODE.CONFIRM_CANCEL,
      Model.getInstance().currentWG
    ).show();
  }

  /**
   * Suppression de la wg
   * @param result Objet de retour sur popUp de confirmation. Contient l'action choisie, et la data d'origine
   */
  private doRemove(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {

    }
  }

  /** desactivation des boutons save et cancel sur updateModel */
  public disableButtons(): boolean {
    let result: boolean = true;
    if (Model.getInstance().cloneWG && Model.getInstance().cloneWG.code && Model.getInstance().cloneWG.labelVO.hasEntry()) {
      result = false;
    }
    if (!result) {
      result = !ViewData.wizUpdateModel;
    }
    return result;
  }

  /** desactivation des boutons save et cancel sur updateModel */
  public disableCancel(): boolean {
    return !ViewData.wizUpdateModel;
  }


  /**
   * Validateur d'unicité du code de la vision
   */
  public uniqueCodeValidator(): WizValidatorFn {
    return (): WizValidationErrors | null => {
      // Pas de gestion d'erreur si le currentChronocode est null
      if (Model.getInstance().cloneWG === null || Model.getInstance().cloneWG === undefined) {
        return null;
      }

      if (
        this.tiCode.wizValue !== null &&
        this.tiCode.wizValue &&
        this.tiCode.wizValue.length > 0 &&
        !Model.getInstance().cloneWG.exist
      ) {
        // Recherche d'une autre avec le même code
        const vmap = Model.getInstance().weightGridEngine.getWeightGridMap();
        let other = null;
        if (!!vmap) {
          other = vmap.get(this.tiCode.wizValue.toUpperCase());
        }

        if (this.tiCode.wizValue.toUpperCase() === ViewData.wildcardMainVision || // pas __SETTINGS
          this.tiCode.wizValue.toUpperCase() === ViewData.wildcardDomainLiteral // ni WIZEOO
        ) {
          const temp: WizValidationError = {
            wizError: '$.common.keyword.$',
            wizErrorParams: [],
          };
          return [temp];
        }


        // Si la vision existe deja avec le meme code on affiche le message
        if (other) {
          const temp: WizValidationError = {
            wizError: '$.vision.error.code.unique.$',
            wizErrorParams: [],
          };
          return [temp];
        }
      }
      return null;
    };
  }


  /** demande de merge sur btOk */
  private doMerge(e: WizEvent): void {
    let errors = this.vgDetailZoneValidator.check(true, true);

    if (errors && errors.length === 0) {
      Model.getInstance().weightGridEngine.mergeWG(Model.getInstance().cloneWG, Model.getInstance().currentWG, this.backFromMerge.bind(this));
    } else {
      WizPopUp.toast('$.wg.error.$', WizIcon.TOAST_ERROR).show();
    }
  }

  /**
   * Callback de l'ajout ou modification d'une vision
   * @param bddBack vision créée ou modifiée (le clone)
   */
  private backFromMerge(bddBack: WeightGridVO): void {
    // Model.getInstance().visionCode = bddBack.code;
    // Model.getInstance().visionEngine.visionDS.refresh();
    // TODO gestion create / update
    this.wgDS.setSource([...Model.getInstance().weightGridEngine.getWeightGrids()]);
    Model.getInstance().currentWG = bddBack;
    Model.getInstance().cloneWG = Model.getInstance().weightGridEngine.cloneWG(Model.getInstance().currentWG);
    this.feed();
  }

  /**
   * revert sur btCancel, simple resélection
   * @param e wizEvent
   */
  private doRevert(e: WizEvent): void {
    // this.visionKeyUpdateFlag = false;
    Model.getInstance().cloneWG = Model.getInstance().weightGridEngine.cloneWG(Model.getInstance().currentWG);
    this.feed();
  }

  /**
   * Modification du model dans la zone de saisie
   * @param e Evenement model change
   */
  private updateModel(e: WizEvent): void {
    // console.log('updateModel', e.value);
    let noRealChange: boolean = false; // attention aux faux positifs sur clic dans les listes dragabbles

    switch (e.component) {
      case this.tiCode:
        Model.getInstance().cloneWG.code = this.tiCode.wizValue?.toUpperCase();
        break;
      case this.tiLabelTrs:
        Model.getInstance().cloneWG.labelVO.updateTranslationEntry(e.value);
        Model.getInstance().cloneWG.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
        break;
    }
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));

  }


  /**
   * Initialisation des valeurs des champs dans le conteneur d'edition en fonction du currentCountry
   */
  public feed(): void {
    if (Model.getInstance().cloneWG !== undefined && Model.getInstance().cloneWG !== null) {
      // alimentation du code
      this.tiCode.wizValue = Model.getInstance().cloneWG.code;
      this.tiCode.wizDisabled =
        Model.getInstance().cloneWG.exist ||
        !Model.getInstance().permissionEngine.hasAnyPermission({
          feature: StaticFeatureEnum.VISION,
          accesses: [PermissionAccess.CREATE, PermissionAccess.UPDATE],
          visibility: PermissionVisibility.DISABLED,
        }); // édition du code
      // alimentation du libellé
      this.tiLabelTrs.translationData = Model.getInstance().localeEngine.buildTranslationData(Model.getInstance().cloneWG.labelVO);

      // focus du premier champs actif
      this.focusFormFields();
      // passe le model update a false
      this.resetModelUpdate();
    } else {
      this.reset();
    }

    super.managePermissions(this.viewPermissions);

    this.vgDetailZone.visible = this.isDetailVisible;
    this.btnRemove.wizDisabled =
      !Model.getInstance().currentWG ||
      !Model.getInstance().currentWG.exist ||
      !Model.getInstance().permissionEngine.canDelete(
        StaticFeatureEnum.VISION,
        Model.getInstance().userAppEngine.newUserApp(
          Model.getInstance().currentWG.domain,
          Model.getInstance().currentWG.businessUnitCode,
          Model.getInstance().currentWG.code
        )
      );
  }


  /**
   * focus du premier champs actif
   */
  public focusFormFields(): void {
    // focus du label si c'est une mise à jour
    if (Model.getInstance().cloneWG.exist) {
      WizFocusManager.getInstance().wizSetFocus(this.tiLabelTrs);
    } else {
      WizFocusManager.getInstance().wizSetFocus(this.tiCode);
    }
  }

  /**
   * mise à false du model update
   */
  public resetModelUpdate() {
    // on informe que le model n'a pas de changement en cours
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, false));
  }

  /** reset complet des champs */
  private reset(): void {
    this.tiCode.wizValue = null;
    this.tiLabelTrs.wizValue = null; // translatedLabel
  }



}
